/**
 * Vendor Revenue Page (Issue #1160, Fixed #1173)
 *
 * Page component for vendor revenue tracking.
 * Wraps RevenueTracker component with authentication and routing.
 *
 * NOTE: This page is already protected by VendorAuthGuard at the route level,
 * so no additional authentication check is needed here.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authApi } from '../services/api';
import { RevenueTracker } from '../components/RevenueTracker';
import { FairSelector } from '../components/FairSelector';

const VendorRevenuePage: React.FC = () => {
  const { fairId: fairIdParam } = useParams<{ fairId: string }>();
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authApi.getCurrentUser();
        setVendorId(user.id);
      } catch (error) {
        console.error('Failed to load user:', error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </div>
    );
  }

  if (!vendorId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">
          Failed to load user information. Please try refreshing the page.
        </div>
      </div>
    );
  }

  const fairId = fairIdParam ? parseInt(fairIdParam, 10) : null;

  if (!fairId || isNaN(fairId) || fairId <= 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">
          Invalid fair ID. Please check the URL and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Revenue Tracking</h1>
          <FairSelector vendorId={vendorId} currentFairId={fairId} basePath="revenue" />
        </div>
        <RevenueTracker vendorId={vendorId} fairId={fairId} />
      </div>
    </div>
  );
};

export default VendorRevenuePage;
